import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RRDLink, useParams } from '@/providers/RouterShim';
import {
  IconChevronLeft,
  IconDirDown,
  IconDirUp,
} from '../../../../styled-components/components/Icon';
import { SrOnly } from '../../../../styled-components/components/SrOnly';
import { ButtonReset } from '../../../../styled-components/layout/reset';
import useMainNavigation, { useMobileMenuNavigation } from '../NavigationProvider';
import type { NavigationPage } from '../NavigationProvider/utils';
import useRouteNotifications, { getNotificationCountByUrl } from '../useRouteNotifications';
import { CloseBtn, Item, ItemLabel, List, Notification, Overflow, Wrapper } from './wrapper';

interface MainNavigationListGroupProps {
  page: NavigationPage;
}

const externalUrlProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
};

const MainNavigationListGroup = ({ page }: MainNavigationListGroupProps) => {
  const { branchId } = useParams();
  const [expanded, setExpanded] = useState(() => !!page.isActive);
  const { onToggleMobileMenu } = useMobileMenuNavigation();

  const notifications = useRouteNotifications();

  useEffect(() => {
    if (page.isActive) {
      setExpanded(page.isActive);
    }
  }, [page.isActive]);

  const notificationCount = useMemo(
    () => getNotificationCountByUrl(notifications, branchId, page.url),
    [notifications, branchId, page.url],
  );

  return (
    <>
      {page.pages?.length && page.pages?.length > 0 ? (
        <Item
          as={ButtonReset}
          onClick={() => setExpanded((s) => !s)}
          title={page.title}
          data-testid={`main-navigation-${page.testId}`}
          $childIsActive={page.isActive}
        >
          <ItemLabel>{page.title}</ItemLabel>
          {!!notificationCount && (
            <Notification>{notificationCount > 99 ? '99+' : notificationCount}</Notification>
          )}
          {expanded ? <IconDirUp /> : <IconDirDown />}
        </Item>
      ) : (
        <Item
          as={RRDLink}
          to={page.url}
          title={page.title}
          data-testid={`main-navigation-${page.testId}`}
          $isActive={page.isActive}
          onClick={onToggleMobileMenu}
          {...(page.externalUrl ? externalUrlProps : {})}
        >
          <ItemLabel>{page.title}</ItemLabel>
          {!!notificationCount && (
            <Notification>{notificationCount > 99 ? '99+' : notificationCount}</Notification>
          )}
        </Item>
      )}
      {!!page.pages?.length && expanded && <MainNavigationList pages={page.pages} />}
    </>
  );
};

interface MainNavigationListProps {
  pages?: NavigationPage[];
}

const MainNavigationList = ({ pages }: MainNavigationListProps) => {
  if (!pages?.length) return null;

  return (
    <List>
      {pages.map((page) => (
        <MainNavigationListGroup key={page.url} page={page} />
      ))}
    </List>
  );
};

interface MainNavigationProps {
  pages?: NavigationPage[];
}

const MainNavigation = ({ pages }: MainNavigationProps) => {
  const { t } = useTranslation(['common', 'mainNavigation']);
  const { expanded, onClose, initialized } = useMainNavigation();

  if (!pages?.length || !expanded || !initialized) return null;

  return (
    <Wrapper id="main-navigation-foldout" tabIndex={-1}>
      <Overflow>
        <MainNavigationList pages={pages} />
      </Overflow>
      <CloseBtn onClick={onClose} title={t('closeMainNavigation', { ns: 'mainNavigation' })}>
        <IconChevronLeft />
        <SrOnly>{t('closeMainNavigation', { ns: 'mainNavigation' })}</SrOnly>
      </CloseBtn>
    </Wrapper>
  );
};

export default MainNavigation;
